import {GlobalConstants} from '@/common/constants';
import appModule from '@/model/store/modules/app-module';
import detailModule from '@/model/store/modules/detail-module';
import userModule from '@/model/store/modules/user-module';
import {GCICategoryListRes} from '@/model/dto/res/contents/get-category-list-res';
import _ from 'lodash';

class ContentsService {

  /**
   * オブジェクトの中身をログに出力
   *
   * @param val: any
   */
  public showObjectConsoleLog(val: any): void {
    if (process.env.NODE_ENV !== 'production') {
      const result: string = JSON.stringify(val);
      console.log(`result =>  ${result}`);
    }
  }

  /**
   * パンくずリストを作成する
   *
   * @param path: string, type: string, category?: string, title?: string
   * ex) home > カテゴリ > 記事タイトル
   */
  public setBreadcrumb(path: string, type: string, category?: string | null, categoryName?: string | null, categoryId?: number | null, title?: string | null, token?: string | null, searchType?: string | null) {
    const info = _.find(appModule.categoryInfo, (val: GCICategoryListRes) => {
      return _.includes(path.slice(1).split('/'), String(val.category_type));
    });
    const apiToken = token ? token : userModule.apiToken;
    const categoryNameStr = categoryName ? categoryName : info ? info.category_name : '';
    let titleStr = title ? title : detailModule.contents.title;
    titleStr = titleStr === '' ? `${GlobalConstants.TEXT.NEWPOST}` : titleStr;
    const envPath = process.env.VUE_APP_PUBLIC_PATH;
    let param: Array<{
      href: string,
      text: string,
      disabled: boolean,
      cid?: string
    }> = [
      {
        href: `${envPath}`,
        text: `${GlobalConstants.TEXT.HOME}`,
        disabled: false
      }
    ];

    switch (type) {
      case 'account':
        titleStr = `${GlobalConstants.TEXT.ACCOUNT.TITLE}`;
        param = [
          {
            href: `${envPath}`,
            text: `${GlobalConstants.TEXT.HOME}`,
            disabled: false
          }
        ];
        break;
      case 'postcountmanage':
        titleStr = `${GlobalConstants.TEXT.POSTCOUNTMANAGE.TITLE}`;
        param = [
          {
            href: `${envPath}`,
            text: `${GlobalConstants.TEXT.HOME}`,
            disabled: false
          }
        ];
        break;
      case 'categorymanage':
        titleStr = `${GlobalConstants.TEXT.CATEGORYMANAGE.TITLE}`;
        param = [
          {
            href: `${envPath}`,
            text: `${GlobalConstants.TEXT.HOME}`,
            disabled: false
          }
        ];
        break;
      case 'search':
        param = [
          {
            href: `${envPath}`,
            text: `${GlobalConstants.TEXT.HOME}`,
            disabled: false
          }
        ];
        break;
      case 'menu':
        param = [
          {
            href: `${envPath}`,
            text: `${GlobalConstants.TEXT.HOME}`,
            disabled: false
          }, {
            href: ``,
            text: `${titleStr}`,
            disabled: true
          }
        ];
        break;
      case 'about':
        param = [
          {
            href: `${envPath}`,
            text: `${GlobalConstants.TEXT.HOME}`,
            disabled: false
          }, {
            href: ``,
            text: `${titleStr}`,
            disabled: true
          }
        ];
        break;
      default:
        titleStr = titleStr === '' ? `${GlobalConstants.TEXT.NEWPOST}` : titleStr;
        param.push({
          href: `${envPath}?cid=${categoryId}`,
          text: `${categoryNameStr}`,
          disabled: type !== 'category' ? false : true,
          cid: `${categoryId}`
        });
        break;
    }
    if (path.slice(1).split('/')[1] && type !== 'category') {
      param.push({
        href: ``,
        text: `${titleStr}`,
        disabled: true
      });
    }
    detailModule.pushBreadcrumbs(param);
  }

  /**
   * ハッシュタグ抽出する
   *
   * @param text: string
   * ex) contentsService.hashtagExtraction('hogehoge') => [hogehoge]
   */
  public hashtagExtraction(text: string): string[] {
    // ハッシュタグ抽出
    if (!text) { return []; }
    return text.match(/[#＃][Ａ-Ｚａ-ｚA-Za-z一-鿆0-9０-９ぁ-ヶｦ-ﾟー._-]+/gm) ?? [];
  }

  /**
   * ハッシュタグがあれば、そのハッシュタグ部分をリンクにした文字列を返す
   * @param {string} 文字列
   * @return {string} タグ部分がurl化された文字列
   */
  public addAnchrInTag(text: string, option: {path: string, target?: string, token?: string}) {
    const hashtag = this.hashtagExtraction(text);
    const token = option?.token ?? userModule.apiToken;
    let val = text;
    _.each(hashtag, (v: string, i: number) => {
      if (option) {
        const path = option.path ?? '';
        const keyword = v.replace(/#/gi, '');
        const link = '<a href="javascript:void(0);" class="' + keyword + '">' + v + '</a>';
        val = val.replace(new RegExp(v, 'gi'), link);
      }
    });
    return val;
  }

  /**
   * 本文の文字数を制御する
   *
   * @param str: string, size: number, suffix: string
   * ex) contentsService.truncateString('hogehoge', 3) => hog...
   */
  public truncateString = (str: string = '', size: number, suffix: string = '...') => {
    const strLength = [...str].reduce((sm: number, char: string) => {
      return sm + (char.charCodeAt(0) <= 255 ? 0.5 : 1);
    }, 0);
    if (strLength <= size) { return str; }

    const suffixSize = [...suffix].reduce((sm: number, char: string) => {
      return sm + (char.charCodeAt(0) <= 255 ? 0.5 : 1);
    }, 0);
    let sum = 0;
    let isOver = false;
    const resultStr = [...str].filter((char: string) => {
      sum += char.charCodeAt(0) <= 255 ? 0.5 : 1;
      if (isOver || sum > size - suffixSize) {
        isOver = true;
        return false;
      }
      return true;
    });
    return `${resultStr.join('')}${suffix}`;
  }

  /**
   * 画像があれば、そのハッシュタグ部分をリンクにした文字列を返す
   * @param {string} 文字列
   * @return {string} タグ部分がurl化された文字列
   */
  public srcExtraction(text: string): string[] {
    if (!text) { return []; }
    const pattern = text.match(/(src=)["|'](.*?)["|']+/g) ?? [];
    const results = _(pattern)
    .map((v: string, i: number) => {
      const match = v.match(/(http|https)?:\/\/[-_.!~*\'()a-zA-Z0-9;\/?:\@&=+\$,%#]+/g) ?? [];
      return match[0];
    })
    .value();
    return results;
  }

  /*
   *  exclude html tag
   */
  public contentsTextRegex(o: string): string {
    return o.replace(/<("[^"]*"|'[^']*'|[^'">])*>/g, '');
  }

}

export default new ContentsService();
